import tokens from "../../../../design-system/tokens";
import VideoEmbed from "../../../../design-system/components/Video/Video.jsx";
import styles from "../../../../design-system/components/PopOver/popOver.css.js";

/**
 * The content tools behind the Spark button, in the order the toolbar renders
 * them. Bibliothek and Glossar are per-spark features; the Showroom is always
 * there. Each carries the sentence the tour uses to introduce it.
 */
const sparkTools = ({ library, glossary }) =>
  [
    library && {
      name: "Bibliothek",
      description: (
        <>
          In der<b> Bibliothek </b>sind kuratierte Inhalte zum Thema hinterlegt.
          Deine Schüler*innen werden während des Sparks dazu motiviert, diese
          zum Stöbern und für die Recherche zu nutzen.
        </>
      ),
    },
    glossary && {
      name: "Glossar",
      description: (
        <>
          Das<b> Glossar </b>erklärt die wichtigsten Begriffe des Sparks. In den
          Inhalten sind sie markiert und lassen sich direkt nachschlagen.
        </>
      ),
    },
    {
      name: "Showroom",
      description: (
        <>
          Der<b> Showroom </b>ermöglicht am Ende des Sparks das Teilen und
          Diskutieren der Projekte.
        </>
      ),
    },
  ].filter(Boolean);

/** "Bibliothek, Glossar & Showroom" — the tools this spark has, as a headline. */
const toolsTitle = (names) =>
  names.length < 2
    ? names.join("")
    : `${names.slice(0, -1).join(", ")} & ${names.at(-1)}`;

/**
 * An array containing step-by-step data utilized for guiding users through a tour in the application.
 *
 * Each step specifies a title, content to display, target elements for highlighting, placement of the instructions,
 * and additional data for controlling the navigation and state of the tour.
 *
 * Properties:
 * - `title`: The headline or brief description of the step.
 * - `content`: The main instructional content to be displayed to the user during the step. It may include JSX elements for styled or interactive content.
 * - `target`: The specific DOM element that should be highlighted during the step.
 * - `placement`: Indicates the position of the tooltip or instruction display relative to the target element (e.g., 'top', 'bottom', 'center').
 * - `data`: Additional data for controlling the step, such as the path for the next step or other contextual metadata.
 * - `isFixed`: Optional boolean to indicate if the tooltip or content for this step should remain fixed in position (doesn't move during scrolling).
 * - `locale`: Optional object for step-specific locale overrides, such as custom text for buttons or instructions.
 *
 * The steps depend on which features the spark has, so this is built per spark
 * rather than being a constant: steps whose target would never mount are left
 * out, and the copy only names the tools this spark actually has.
 *
 * @param {Object} features - The spark's feature flags.
 * @param {boolean} [features.library] - Whether the Bibliothek is enabled.
 * @param {boolean} [features.glossary] - Whether the Glossar is enabled.
 * @returns {Array<Object>} The tour steps for this spark.
 */
export const buildStepsForUserTour = ({ library, glossary }) => {
  const tools = sparkTools({ library, glossary });

  return [
    //Lernreise
    {
      title: "Geschafft, deine Lernreise ist bereit!",
      content: (
        <>
          <p style={{ marginBottom: tokens.space[24] }}>
            Schaue dir die Digital Sparks Lernreise an, bereite alles vor und
            lade sobald du soweit bist, deine Schüler*innen ein!
          </p>
          <VideoEmbed url="https://vimeo.com/1060441465?share=copy" />
        </>
      ),
      target: "#title",
      placement: "center",
      locale: {
        next: <p>Jetzt loslegen!</p>,
      },
      showSkipButton: false,
      styles: {
        buttonNext: styles.buttonOnly,
      },
    },
    {
      title: "Lernreise (Zeitplan)",
      content: (
        <>
          Willkommen in deiner Lernreise. Diese Übersicht, wir nennen sie auch
          „Zeitplan“, zeigt dir, wie die Lernreise aufgebaut ist.{" "}
          <b>Deine Schüler*innen sehen diesen Zeitplan genau wie du.</b> Es gibt
          aber auch Unterschiede, dazu kommen wir später.
        </>
      ),
      target: "#title",
      placement: "auto",
      isFixed: true,
    },
    {
      title: "Phasen",
      content:
        "Hier siehst du die Aufteilung der Etappen der Lernreise in die verschiedenen Phasen: Discover, Create und Share. Jeder Spark folgt diesen Phasen.",
      target: "#higher-level-navigation",
      placement: "right",
      isFixed: true,
    },
    {
      title: "Etappen",
      content:
        "Hier kannst du durch die verschiedenen Etappen deiner Lernreise navigieren.",
      target: "#chapter-navigation",
      placement: "right",
      isFixed: true,
    },
    {
      title: "Stationen",
      content:
        "In den verschiedenen Stationen einer Etappe finden sich chronologisch aufeinander aufbauende Lerninhalte und Aufgaben.",
      target: "#chapter-schedule",
      placement: "left-start",
      isFixed: true,
      data: {
        next: (slug) => `step/${slug}`,
      },
    },
    //Slides
    {
      title: "Inhalte",
      content:
        "Über Texte, Videos, Illustrationen, Aufgaben, Links und interaktive Inhalte gewinnen deine Schüler*innen vielfältige Perspektiven auf das Thema.",
      target: "#page",
      placement: "center",
      isFixed: true,
      data: {
        prev: () => -1,
      },
    },
    {
      title: "Navigation",
      content:
        "Mit den Pfeilen klickst du dich durch die Inhalte der Stationen.",
      target: "#step-navigation",
      placement: "bottom",
      isFixed: true,
    },
    {
      title: "Zurück zur Übersicht",
      content: "Über den „Schließen“-Button gelangst du zum Zeitplan zurück.",
      target: "#close-button",
      placement: "left-start",
      isFixed: true,
      data: {
        next: () => -1,
      },
    },
    //Wieder zurück zur Lernreise
    {
      title: "Toolbar",
      content: (
        <>
          Deine Toolbar zeigt dir alle wichtigen Funktionen deiner Lernreise.
          <b>
            {" "}
            Wichtig: Deine Schüler*innen sehen eine etwas andere Toolbar - sie
            können z.B. nicht Personen zur Lernreise einladen oder Einstellungen
            vornehmen.
          </b>
        </>
      ),
      target: "#toolbar",
      placement: "auto",
      isFixed: true,
      data: {
        prev: (slug) => `step/${slug}`,
      },
    },
    {
      title: "Klassenzimmer",
      content: (
        <>
          Mit dem Klassenzimmer Feature kannst du vorsichtig Einfluss auf die
          App deiner Schüler*innen nehmen. Pausiere alle Bildschirme oder sende
          eine Nachricht an deine Klasse.
        </>
      ),
      target: "#classroom-button",
      placement: "top",
      isFixed: true,
    },
    {
      // Points into the open flyout, so the entries it describes are on screen
      // while it does — `openSparkMenu` is what holds the flyout open.
      title: toolsTitle(tools.map((tool) => tool.name)),
      content: (
        <>
          {tools.map((tool) => (
            <p key={tool.name} style={{ marginBottom: tokens.space[16] }}>
              {tool.description}
            </p>
          ))}
        </>
      ),
      target: "#spark-menu",
      placement: "top",
      isFixed: true,
      data: {
        openSparkMenu: true,
      },
    },
    {
      title: "Handbuch",
      content:
        "In deinem Handbuch erhältst du Hilfestellung, um deine Lernreise vorzubereiten, Antworten zu häufig gestellten Fragen, Videoerklärungen und einen Spickzettel zum Ausdrucken, den du während der Durchführung mitnehmen kannst.",
      target: "#handbook-button",
      placement: "top",
      isFixed: true,
    },
    {
      title: "Einführung erneut anschauen?",
      content:
        "Wenn du dir die Einführung später erneut anschauen magst, findest du sie in den Optionen.",
      target: "#options-button",
      placement: "top",
      isFixed: true,
    },
    {
      title: "Geschafft, lege los sobald du soweit bist.",
      content: (
        <>
          Lade deine Schüler*innen ein, lies ein wenig im Handbuch oder mache
          dich noch weiter mit den Inhalten vertraut, indem du dich durch den
          Zeitplan klickst. <b> Schön, dass du da bist :)</b>
        </>
      ),
      target: "#handbook-button",
      placement: "center",
      isFixed: true,
    },
  ].filter(Boolean);
};
